import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(() => {
    return localStorage.getItem('exercicios-banner-dismissed') !== 'true';
  });
  const location = useLocation();

  const dismissBanner = () => {
    setBannerVisible(false);
    localStorage.setItem('exercicios-banner-dismissed', 'true');
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const handleLinkClick = () => setMobileMenuOpen(false);

  const handleOpenChat = () => {
    setMobileMenuOpen(false);
    const btn = document.querySelector('[data-readdy-agent-trigger]') as HTMLElement | null;
    if (btn) {
      btn.click();
    } else {
      const all = document.querySelectorAll('button');
      for (const b of Array.from(all)) {
        if (b.getAttribute('aria-label')?.toLowerCase().includes('chat') || b.className.includes('readdy-agent')) {
          b.click();
          break;
        }
      }
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === '/' || location.pathname === '/home') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navTop = bannerVisible ? 'top-10' : 'top-0';

  return (
    <>
      {/* Announcement Banner */}
      {bannerVisible && (
        <div className="fixed top-0 left-0 right-0 z-[55] bg-[#0a0a0a] border-b border-gold-500/20 h-10 flex items-center px-6">
          <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-4">
            <div className="flex-1 text-center">
              <Link
                to="/exercicios-gratis"
                onClick={dismissBanner}
                className="inline-flex items-center gap-2 text-sm font-medium tracking-wide transition-colors group"
              >
                <span className="text-gold-400 text-xs">✦</span>
                <span className="text-white/70 group-hover:text-white transition-colors">
                  12 exercícios de PNL totalmente gratuitos disponíveis
                </span>
                <span className="text-gold-400 font-semibold group-hover:text-gold-300 transition-colors">
                  Experimentar agora →
                </span>
              </Link>
            </div>
            <button
              onClick={dismissBanner}
              className="text-white/30 hover:text-white/60 transition-colors flex-shrink-0 cursor-pointer"
              aria-label="Fechar"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>
      )}

      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${navTop} bg-[#0a0a0a] ${
          scrolled ? 'border-b border-gold-500/20 backdrop-blur-md' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3 group cursor-pointer">
              <img
                src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
                alt="Bia Holmes"
                className="w-[72px] h-[72px] object-contain rounded-full border border-gold-500/30 p-0.5 flex-shrink-0 group-hover:scale-110 group-hover:drop-shadow-[0_0_14px_rgba(201,164,64,0.55)] transition-all duration-300"
              />
              <div className="flex flex-col">
                <span className="font-display font-semibold text-2xl tracking-wide text-white">
                  Bia Holmes
                </span>
                <span className="text-sm tracking-widest uppercase text-gold-400">
                  Método de Transformação
                </span>
              </div>
            </Link>

            {/* Menu Desktop */}
            <div className="hidden md:flex items-center gap-7">
              {[
                { to: '/sobre', label: 'Sobre' },
                { to: '/planos', label: 'Planos' },
                { to: '/blog', label: 'Blog' },
                { to: '/depoimentos', label: 'Depoimentos' },
                { to: '/faq', label: 'FAQ' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-sm font-medium tracking-wide transition-colors hover:text-gold-400 text-white/75 whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/programa-diario"
                className="text-sm font-medium tracking-wide transition-colors hover:text-gold-400 text-white/75 flex items-center gap-1.5 whitespace-nowrap"
              >
                Programa Diário
                <span className="px-2 py-0.5 bg-gold-500/15 text-gold-400 border border-gold-500/30 text-xs rounded-full font-semibold tracking-wide">
                  NOVO
                </span>
              </Link>
              <Link
                to="/exercicios-gratis"
                className="text-sm font-medium tracking-wide transition-colors hover:text-gold-400 text-white/75 flex items-center gap-1.5 whitespace-nowrap"
              >
                Exercícios
                <span className="px-2 py-0.5 bg-gold-500/10 text-gold-400 border border-gold-500/30 text-xs rounded-full font-semibold tracking-wide">
                  GRÁTIS
                </span>
              </Link>
              <button
                onClick={handleOpenChat}
                className="flex items-center gap-1.5 px-4 py-2 border border-gold-500/40 text-gold-400 text-sm font-semibold rounded-full hover:bg-gold-500/10 hover:border-gold-500/70 transition-all whitespace-nowrap tracking-wide cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-customer-service-2-line text-sm"></i>
                </div>
                Falar com a Bia
              </button>
              <Link
                to="/auth/login"
                className="px-5 py-2 bg-gradient-to-r from-gold-600 to-gold-500 text-[#0a0a0a] text-sm font-bold rounded-full hover:from-gold-700 hover:to-gold-600 transition-all whitespace-nowrap tracking-wide"
              >
                Entrar
              </Link>
            </div>

            {/* Botão Menu Mobile */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Abrir menu"
            >
              <i className="ri-menu-line text-2xl text-white"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Menu Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-80 bg-[#0a0a0a] border-l border-gold-500/15 z-[70] md:hidden transform transition-transform duration-300 ease-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gold-500/15">
            <button onClick={handleLogoClick} className="flex items-center gap-3 cursor-pointer group">
              <img
                src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
                alt="Bia Holmes"
                className="w-[56px] h-[56px] object-contain rounded-full border border-gold-500/30 p-0.5 flex-shrink-0"
              />
              <div className="flex flex-col">
                <span className="font-display font-semibold text-xl text-white">Bia Holmes</span>
                <span className="text-xs text-gold-400 tracking-widest uppercase">Método</span>
              </div>
            </button>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center hover:bg-gold-500/10 rounded-lg transition-colors"
              aria-label="Fechar menu"
            >
              <i className="ri-close-line text-2xl text-white/70"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-6">
            <div className="px-6 space-y-1">
              {[
                { to: '/sobre', icon: 'ri-information-line', label: 'Sobre o Método' },
                { to: '/planos', icon: 'ri-vip-crown-line', label: 'Planos' },
                { to: '/blog', icon: 'ri-article-line', label: 'Blog' },
                { to: '/depoimentos', icon: 'ri-chat-quote-line', label: 'Depoimentos' },
                { to: '/faq', icon: 'ri-question-answer-line', label: 'FAQ' },
                { to: '/imprensa', icon: 'ri-newspaper-line', label: 'Imprensa' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-gold-500/8 hover:text-gold-400 rounded-xl transition-colors"
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${item.icon} text-lg text-gold-500`}></i>
                  </div>
                  <span className="font-medium tracking-wide">{item.label}</span>
                </Link>
              ))}
              <Link
                to="/programa-diario"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-gold-500/8 hover:text-gold-400 rounded-xl transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-lg text-gold-500"></i>
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <span className="font-medium tracking-wide">Programa Diário</span>
                  <span className="px-2 py-0.5 bg-gold-500/15 text-gold-400 border border-gold-500/30 text-xs rounded-full font-semibold">NOVO</span>
                </div>
              </Link>
              <Link
                to="/exercicios-gratis"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-gold-500/8 hover:text-gold-400 rounded-xl transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-mental-health-line text-lg text-gold-500"></i>
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <span className="font-medium tracking-wide">Exercícios de PNL</span>
                  <span className="px-2 py-0.5 bg-gold-500/10 text-gold-400 border border-gold-500/25 text-xs rounded-full font-semibold">GRÁTIS</span>
                </div>
              </Link>
            </div>
          </div>

          <div className="p-6 border-t border-gold-500/15 flex flex-col gap-3">
            <button
              onClick={handleOpenChat}
              className="flex items-center justify-center gap-2 w-full px-6 py-3 border border-gold-500/40 text-gold-400 font-semibold rounded-full hover:bg-gold-500/10 transition-all whitespace-nowrap tracking-wide cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-lg"></i>
              </div>
              Falar com a Bia
            </button>
            <Link
              to="/auth/login"
              onClick={handleLinkClick}
              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-[#0a0a0a] font-bold rounded-full hover:from-gold-700 hover:to-gold-600 transition-all whitespace-nowrap tracking-wide"
            >
              <i className="ri-login-box-line text-xl"></i>
              Entrar
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
